import { useCallback, useRef } from 'react'

import { useDeepCompare } from '@hozana/hooks/useDeepCompare'
import type { TGTMVariables } from '@hozana/tracking/constants'
import { GTM } from '@hozana/tracking/gtm'

/**
 * ### useTrackOnlyOnce ###
 *
 * Memorize tracked events as long as not unmounted
 *
 * @param defaultVariables (Optional) Default variables that will be passed to all the tracked events
 * Each default variable can be overwritten.
 * @returns A GTM.trackEvent-like trackEvent function. Each variable given to this function will overwrite
 * the corresponding one in defaultVariables.
 */
export const useTrackOnlyOnce = (defaultVariables: TGTMVariables = {}): typeof GTM.trackEvent => {
  const trackedEventsRef = useRef([])

  const memDefaultVariables = useDeepCompare([defaultVariables])

  const trackEvent = useCallback(
    (event, variables = {}, nativeEvent = undefined, preserveCase = false) => {
      const eventName = typeof event === 'object' ? event.event : event
      if (!trackedEventsRef.current.includes(eventName)) {
        trackedEventsRef.current.push(eventName)
        GTM.trackEvent(event, { ...memDefaultVariables, ...variables }, nativeEvent, preserveCase)
      }
    },
    [memDefaultVariables],
  )

  return trackEvent
}
